import { useCallback, useMemo } from 'react';
import { useOrganizationContext } from '../../context/OrganizationContext';
import useSWR from 'swr';
import { getHashMap, HashMap } from '../../utils/objectTransformers';
import { ReservationType } from '../../types/reservationType';
import { useClient } from '../useClient';
import { capitalize } from '../../utils/textUtils';

// Data hues of the reservation types, by type order. The first is indigo, not `primary`: the primary ramp follows the accent,
// which is the brand yellow in the dark theme and would look like the fourth type.
export const RESERVATION_COLORS = ['indigo', 'green', 'blue', 'yellow', 'orange', 'cyan', 'red'];

export interface ReservationTypeWithColor extends ReservationType {
  color: string;
  tagColor: string;
}
type ReservationTypeHashMap = HashMap<'id', ReservationTypeWithColor>;

export const useReservationTypes = () => {
  const { organization, organizationLoading } = useOrganizationContext();
  const { get } = useClient();

  const reservationTypesFetcher = useCallback(
    async (url: string): Promise<ReservationType[] | undefined> => {
      const reservationTypes = await get<ReservationType[] | undefined>(url);
      if (!reservationTypes) return;
      return reservationTypes;
    },
    [get],
  );

  const {
    data: rawReservationTypes,
    isLoading: reservationTypesLoading,
    error: reservationTypesError,
    mutate: mutateReservationTypes,
  } = useSWR(organization ? `/organizations/${organization.id}/reservation-types` : null, reservationTypesFetcher, {
    revalidateIfStale: false,
    revalidateOnReconnect: false,
  });

  const reservationTypes = useMemo((): ReservationTypeWithColor[] | undefined => {
    if (!rawReservationTypes) return undefined;

    const nameColorMap = new Map<string, string>();
    let colorIndex = 0;

    return rawReservationTypes.map((type) => {
      let color;
      if (nameColorMap.has(type.name)) {
        color = nameColorMap.get(type.name)!;
      } else {
        color = RESERVATION_COLORS[colorIndex % RESERVATION_COLORS.length];
        nameColorMap.set(type.name, color);
        colorIndex++;
      }

      return { ...type, color, tagColor: color };
    });
  }, [rawReservationTypes]);

  const reservationTypesHashMap: ReservationTypeHashMap | undefined = useMemo(
    () => (reservationTypes ? getHashMap(reservationTypes, 'id') : undefined),
    [reservationTypes],
  );

  const reservationTypeFromId = useCallback(
    (reservationTypeId: string) => {
      return reservationTypesHashMap?.[reservationTypeId as keyof ReservationTypeHashMap];
    },
    [reservationTypesHashMap],
  );

  const uniqueReservationTypes = useMemo(() => {
    if (!reservationTypes) return [];

    // Crear un Map usando el nombre como clave para eliminar duplicados
    const uniqueTypes = new Map();
    reservationTypes.forEach((type) => {
      if (!uniqueTypes.has(type.name)) {
        uniqueTypes.set(type.name, type);
      }
    });

    return Array.from(uniqueTypes.values());
  }, [reservationTypes]);

  const findReservationTypeByBranchAndName = useCallback(
    (branchId: string | undefined, name: string | undefined) => {
      if (!branchId || !name) return;
      return reservationTypes?.find((_) => _.branchId === branchId && _.name === name);
    },
    [reservationTypes],
  );

  const reservationTypeFeatures = useMemo(() => {
    return reservationTypes?.flatMap((type) => Object.values(type.features) as string[]);
  }, [reservationTypes]);

  const getMainReservationType = useCallback(() => {
    return reservationTypes?.find((type) => type.order === 0);
  }, [reservationTypes]);

  /**
   * Name of the only type that counts as a visit (`countsAsVisit`). Types are per branch, so names are compared, not
   * ids. Undefined when no type has the flag (every type counts) or when several names have it.
   */
  const visitTypeName = useMemo(() => {
    const names = new Set(reservationTypes?.filter((type) => type.countsAsVisit).map((type) => type.name.trim().toLowerCase()));
    return names.size === 1 ? [...names][0] : undefined;
  }, [reservationTypes]);

  /** The visit type name as the organization typed it (first flagged type), so "TRX" stays "TRX". */
  const visitTypeDisplayName = useMemo(() => {
    if (!visitTypeName) return undefined;
    const name = reservationTypes?.find((type) => type.countsAsVisit)?.name.trim();
    return name ? capitalize(name) : undefined;
  }, [reservationTypes, visitTypeName]);

  /** Adds the visit type to the label of a visit metric: "Días sin venir (Gimnasio)". */
  const withVisitTypeSuffix = useCallback(
    (label: string) => (visitTypeDisplayName ? `${label} (${visitTypeDisplayName})` : label),
    [visitTypeDisplayName],
  );

  return {
    reservationTypes,
    reservationTypesLoading: reservationTypesLoading || organizationLoading,
    reservationTypesError,
    uniqueReservationTypes,
    reservationTypeFromId,
    mutateReservationTypes,
    findReservationTypeByBranchAndName,
    getMainReservationType,
    reservationTypeFeatures,
    visitTypeName,
    withVisitTypeSuffix,
  };
};
